import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Paper
} from '@mui/material';
import { FaUserTie, FaUserGraduate, FaChalkboardTeacher, FaPlus } from 'react-icons/fa';
import Header from '../../components/header';
import Footer from '../../components/footer';
import DataTable from '../../components/DataTable';
import institutionService from '../../services/institutionService';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import './style.css'; // Assume we create this or use existing global
import StudentProfileModal from '../../components/StudentProfileModal';

// --- Stats Card Component ---
const StatsCard = ({ title, value, icon, color }) => (
    <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
            <Box sx={{
                mr: 2,
                p: 2,
                borderRadius: '50%',
                backgroundColor: `${color}20`,
                color: color,
                display: 'flex'
            }}>
                {icon}
            </Box>
            <Box>
                <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                    {value}
                </Typography>
            </Box>
        </CardContent>
    </Card>
);

const InstitutionDashboard = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Data States
    const [institution, setInstitution] = useState(null);
    const [stats, setStats] = useState({ total_teachers: 0, total_students: 0, active_licenses: 0 });
    const [teachers, setTeachers] = useState([]);
    const [students, setStudents] = useState([]);

    // Modal States
    const [openTeacherModal, setOpenTeacherModal] = useState(false);
    const [openStudentModal, setOpenStudentModal] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '', last_name: '', email: '', password: '', bio: '', qualifications: ''
    });

    const fetchAllData = useCallback(async () => {
        if (!user?.institution_id) return;
        setLoading(true);
        try {
            const [instData, statsData, teachersData, studentsData] = await Promise.all([
                institutionService.getInstitution(user.institution_id).catch(() => null), // Fallback if regular get fails, try /me
                institutionService.getStats(user.institution_id),
                institutionService.getTeachers(user.institution_id),
                institutionService.getStudents(user.institution_id)
            ]);

            // If instData failed, try getMyInstitution
            if (!instData) {
                const myInst = await institutionService.getMyInstitution();
                setInstitution(myInst);
            } else {
                setInstitution(instData);
            }

            setStats(statsData);
            setTeachers(teachersData);
            setStudents(studentsData);
        } catch (err) {
            console.error(err);
            setError("Failed to load dashboard data.");
        } finally {
            setLoading(false);
        }
    }, [user?.institution_id]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddTeacher = async () => {
        try {
            await institutionService.addTeacher(user.institution_id, formData);
            setOpenTeacherModal(false);
            setFormData({ first_name: '', last_name: '', email: '', password: '', bio: '', qualifications: '' });
            fetchAllData(); // Refresh
        } catch (err) {
            alert(err.response?.data?.detail || "Failed to add teacher");
        }
    };

    const handleAddStudent = async () => {
        try {
            await institutionService.addStudent(user.institution_id, {
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                password: formData.password
            });
            setOpenStudentModal(false);
            setFormData({ first_name: '', last_name: '', email: '', password: '', bio: '', qualifications: '' });
            fetchAllData(); // Refresh
        } catch (err) {
            alert(err.response?.data?.detail || "Failed to add student");
        }
    };

    const [selectedStudentId, setSelectedStudentId] = useState(null);

    const handleViewDetails = (studentId) => {
        setSelectedStudentId(studentId);
    };

    const handleCloseModal = () => {
        setSelectedStudentId(null);
    };

    const fetchStudentProgress = useCallback((studentId) => {
        return institutionService.getStudentProgress(user.institution_id, studentId);
    }, [user.institution_id]);

    // Columns Definitions
    const teacherColumns = [
        { id: 'name', label: 'Name', render: (row) => `${row.user.first_name} ${row.user.last_name}` },
        { id: 'email', label: 'Email', render: (row) => row.user.email },
        { id: 'active_assignments', label: 'Active Students', render: (row) => '0' }, // Placeholder
        {
            id: 'actions', label: 'Actions', render: (row) => (
                <Button size="small" color="error" onClick={async () => {
                    if (window.confirm('Delete Teacher?')) {
                        await institutionService.deleteTeacher(user.institution_id, row.id);
                        fetchAllData();
                    }
                }}>Delete</Button>
            )
        }
    ];

    const studentColumns = [
        { id: 'name', label: 'Name', render: (row) => `${row.first_name} ${row.last_name}` },
        { id: 'email', label: 'Email', render: (row) => row.email },
        { id: 'progress', label: 'Progress', render: (row) => '0%' }, // Placeholder
        { id: 'status', label: 'Status', render: (row) => row.is_active ? 'Active' : 'Inactive' },
        {
            id: 'actions', label: 'Actions', render: (row) => (
                <Button size="small" variant="outlined" onClick={() => handleViewDetails(row.id)}>
                    View Details
                </Button>
            )
        }
    ];

    if (!user || user.role !== 'institution_admin') {
        return <div className="page-wrapper"><Header /><div className="container py-5">Access Denied</div><Footer /></div>;
    }

    return (
        <div className="page-wrapper">
            <Header />
            <div className="wpo-team-section section-padding" style={{ backgroundColor: '#f5f5f5', minHeight: '80vh' }}>
                <div className="container">
                    <div className="row mb-4">
                        <div className="col-12">
                            <Typography variant="h4" gutterBottom>
                                {t('dashboard.institutionWelcome', { name: institution?.name || 'Institution', defaultValue: 'Dashboard' })}
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Management Portal
                            </Typography>
                        </div>
                    </div>

                    {/* Stats Overview */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <StatsCard
                                title="Total Teachers"
                                value={stats.total_teachers}
                                icon={<FaChalkboardTeacher size={24} />}
                                color="#1976d2"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <StatsCard
                                title="Total Students"
                                value={stats.total_students}
                                icon={<FaUserGraduate size={24} />}
                                color="#2e7d32"
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <StatsCard
                                title="Active Licenses"
                                value={stats.active_licenses}
                                icon={<FaUserTie size={24} />}
                                color="#ed6c02"
                            />
                        </Grid>
                    </Grid>

                    {/* Tabs Area */}
                    <Paper sx={{ width: '100%', mb: 2 }}>
                        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tab label="Teachers" />
                            <Tab label="Students" />
                        </Tabs>

                        {/* Teachers Tab */}
                        <div role="tabpanel" hidden={activeTab !== 0} style={{ padding: '20px' }}>
                            {activeTab === 0 && (
                                <>
                                    <DataTable
                                        columns={teacherColumns}
                                        data={teachers}
                                        loading={loading}
                                        count={teachers.length}
                                        page={1}
                                        actions={
                                            <Button
                                                variant="contained"
                                                startIcon={<FaPlus />}
                                                onClick={() => setOpenTeacherModal(true)}
                                            >
                                                Add Teacher
                                            </Button>
                                        }
                                    />
                                </>
                            )}
                        </div>

                        {/* Students Tab */}
                        <div role="tabpanel" hidden={activeTab !== 1} style={{ padding: '20px' }}>
                            {activeTab === 1 && (
                                <>
                                    <DataTable
                                        columns={studentColumns}
                                        data={students}
                                        loading={loading}
                                        count={students.length}
                                        page={1}
                                        actions={
                                            <Button
                                                variant="contained"
                                                startIcon={<FaPlus />}
                                                onClick={() => setOpenStudentModal(true)}
                                            >
                                                Add Student
                                            </Button>
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </Paper>
                </div>
            </div>
            <Footer />

            {/* Add Teacher Modal */}
            <Dialog open={openTeacherModal} onClose={() => setOpenTeacherModal(false)}>
                <DialogTitle>Add New Teacher</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" name="first_name" label="First Name" fullWidth variant="outlined" onChange={handleFormChange} value={formData.first_name} />
                    <TextField margin="dense" name="last_name" label="Last Name" fullWidth variant="outlined" onChange={handleFormChange} value={formData.last_name} />
                    <TextField margin="dense" name="email" label="Email Address" type="email" fullWidth variant="outlined" onChange={handleFormChange} value={formData.email} />
                    <TextField margin="dense" name="password" label="Password" type="password" fullWidth variant="outlined" onChange={handleFormChange} value={formData.password} />
                    <TextField margin="dense" name="bio" label="Bio (Optional)" fullWidth variant="outlined" multiline rows={2} onChange={handleFormChange} value={formData.bio} />
                    <TextField margin="dense" name="qualifications" label="Qualifications (Optional)" fullWidth variant="outlined" onChange={handleFormChange} value={formData.qualifications} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTeacherModal(false)}>Cancel</Button>
                    <Button onClick={handleAddTeacher} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>

            {/* Add Student Modal */}
            <Dialog open={openStudentModal} onClose={() => setOpenStudentModal(false)}>
                <DialogTitle>Add New Student</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" name="first_name" label="First Name" fullWidth variant="outlined" onChange={handleFormChange} value={formData.first_name} />
                    <TextField margin="dense" name="last_name" label="Last Name" fullWidth variant="outlined" onChange={handleFormChange} value={formData.last_name} />
                    <TextField margin="dense" name="email" label="Email Address" type="email" fullWidth variant="outlined" onChange={handleFormChange} value={formData.email} />
                    <TextField margin="dense" name="password" label="Password" type="password" fullWidth variant="outlined" onChange={handleFormChange} value={formData.password} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenStudentModal(false)}>Cancel</Button>
                    <Button onClick={handleAddStudent} variant="contained">Add</Button>
                </DialogActions>
            </Dialog>

            <StudentProfileModal
                open={!!selectedStudentId}
                onClose={handleCloseModal}
                studentId={selectedStudentId}
                fetchProgressFn={fetchStudentProgress}
            />
        </div>
    );
};

export default InstitutionDashboard;
